import { typographyTokens } from '../../theme/typographyTokens';
import React, { useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import { useGetUserQuery } from '../../services/usersSlice';
import { useGetUserPhotoQuery } from '../../services/microsoftSlice';
import { stringAvatar, generateColorHsl } from '../../utils/avatarGenerator';
import { Grid, Typography, useTheme } from '@mui/material';
import { User, UserShort } from '../../../shared/types/User';

interface UserDisplayProps {
  /** A user ID from Azure Active Directory OR "me" */
  userID?: string | undefined | null;
  /** The user model info, if available. If not given or undefined, the user data will be requested from Azure Active Directory  */
  user?: UserShort | User | undefined;
  /** Whether to show the user's picture */
  showPicture?: boolean;
  /** Whether to show the user's name */
  showName?: boolean;
  /** Whether to show the user's email */
  showEmail?: boolean;
  /** If given, will show the given title below the user's name. Could be "requestor" or the relevant stakeholder role of the user (such as "Legal" or "Security"). */
  title?: string | undefined;
  /** Optional sx prop for Avatar styling */
  avatarSx?: object;
}

/**
 * UserDisplay component
 * --------------------
 * Displays a user's avatar, name, email, and optional title.
 * Fetches user info and photo from Azure AD via RTK Query hooks.
 * Handles error and loading states for user photo, falling back to a generated avatar if photo is missing or errored.
 *
 * Props:
 * - userID: Azure AD user ID or "me" (string | undefined | null)
 * - user: User object (optional, if already available)
 * - showPicture: Whether to show the user's avatar/photo
 * - showName: Whether to show the user's name
 * - showEmail: Whether to show the user's email
 * - title: Optional title/role to display below the name
 */
const UserDisplay = ({
  userID,
  showName,
  showEmail,
  showPicture,
  title,
  user,
  avatarSx,
}: UserDisplayProps) => {

  // Fetch user photo from Microsoft Graph API using RTK Query
  // Returns { data, isFetching, error }
  const { data: userPhoto, isFetching: isPhotoLoading, error: userPhotoError } = useGetUserPhotoQuery(
    userID as string | undefined ,
    { skip: !userID }  
  );
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Fetch user info from Azure AD using RTK Query
  // Skips if user prop is provided, userID is missing, or is a system task
  const { data: userADInfo } = useGetUserQuery(userID as string | undefined, {
    skip: !!user || !userID || userID === 'System Tracking Task',
  });

  // Local state for photo URL to avoid caching issues and ensure correct updates
  const [userPhotoURL, setUserPhotoURL] = React.useState<string | null>(null);

  // Determine user info: prefer prop, fallback to AD info
  const userInfo = user || userADInfo;
  const userName: string =
    userInfo?.givenName && userInfo?.surname
      ? `${userInfo?.givenName} ${userInfo?.surname}`
      : userInfo?.displayName || '';
  const userEmail: string = userInfo?.email || '';

  // Reset photo URL when userID changes to avoid stale/cached images
  useEffect(() => {
    setUserPhotoURL(null);
  }, [userID]);

  // Update photo URL when photo data or error changes
  useEffect(() => {
    if (!isPhotoLoading) {
      if (userPhotoError) {
        // If error (e.g. 404), fallback to generated avatar
        setUserPhotoURL(null);
      } else {
        // If photo available, set photo URL; else fallback
        setUserPhotoURL(userPhoto ? userPhoto : null);
      }
    }
  }, [userPhoto, isPhotoLoading, userPhotoError]);


  return (userID === 'System Tracking Task' ? (
    <div>System Tracking Task</div>
  ) : userID ? (
    <Grid
      container
      wrap="nowrap"
      alignItems="center"
      title={userInfo?.displayName + ' | ' + userEmail}
    >
      {showPicture && (
        <Grid
          item
          sx={{
            marginRight: showName || showEmail || title ? 1 : 0,
          }}
        >
          {isPhotoLoading ? (
            <Avatar
              aria-label="Loading profile picture"
              alt="Loading profile picture"
              sx={{ bgcolor: '#e0e0e0', ...(avatarSx || {}) }}
            >
              ...
            </Avatar>
          ) : userPhotoURL ? (
            <Avatar
              aria-label="Profile picture"
              alt="Profile picture"
              src={userPhotoURL}
              sx={avatarSx}
            />
          ) : (
            <Avatar
              sx={{ bgcolor: generateColorHsl(userName), ...(avatarSx || {}) }}
              {...stringAvatar(userName)}
              aria-label="Generated profile picture"
              alt="Generated profile picture"
            />
          )}
        </Grid>
      )}
      <Grid item>
        {showName && (
          <Typography color={isDarkMode ? theme.palette.white.main :"gray.main"} fontSize={typographyTokens.fontSizes.body}>
            {userName}
          </Typography>
        )}
        {showEmail && (
          <Typography color="gray.darkGray1" fontSize={typographyTokens.fontSizes.caption}>
            {userEmail}
          </Typography>
        )}
        {title && (
          <Typography color="gray.darkGray1" fontSize={typographyTokens.fontSizes.caption}>
            {title}
          </Typography>
        )}
      </Grid>
    </Grid>
  ) : (
    <Typography color="gray.darkGray1" fontSize={typographyTokens.fontSizes.caption}>
      None
    </Typography>
  ));
};

export default UserDisplay;
